import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@leandredev/common_ng_14';
import { IDcbmService, ITemplateAct, IUserAdmin } from '@leandredev/dcbm-common-lib/dist/interfaces';
import { Observable, filter, map, of, startWith, switchMap } from 'rxjs';
import { ActsService } from 'src/app/services/acts/acts.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { InputErrorStateMatcher } from 'src/app/shell/helpers/ImputErrorStateMacher';
import { IActCreationFG, IActCreationParameters } from 'src/app/utils/interfaces';

import { defaultTypes as DefaultTypes } from 'src/environments/configs';
import { I_DefaultTypes } from 'src/environments/interface';

@Component({
  selector: 'app-act-creation',
  templateUrl: './act-creation.component.html',
  styleUrls: ['./act-creation.component.scss']
})
export class ActCreationComponent extends BaseComponent implements OnInit {
  public templates: ITemplateAct[] = [];

  public actCreationFG: FormGroup<IActCreationFG> = new FormGroup({
    template: new FormControl(null, [Validators.required]),
    name: new FormControl(null, [Validators.required]),
    description: new FormControl(null),
    service: new FormControl(null, [Validators.required])
  });

  public matcherTemplate = new InputErrorStateMatcher();

  constructor(
    private actService: ActsService,
    @Inject(MAT_DIALOG_DATA) public defaults: { className?: string; defaultTypeIds?: string[] },
    private dialogRef: MatDialogRef<ActCreationComponent>,
    private profileService: ProfileService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.actService.getActType();

    const updateTemplateByFilter = this.updateTemplateByFilter()
      .pipe(
        map(() => {
          return (this.filteredTemplates = this.actCreationFG.controls.template.valueChanges.pipe(
            startWith(''),
            map((value) => {
              const name = typeof value === 'string' ? value : value?.name;
              return name ? this._filterTemplates(name as string) : this.templates.slice();
            })
          ));
        })
      )
      .subscribe();

    this.subscription.add(updateTemplateByFilter);

    const findService = this.profileService.profile
      .pipe(
        filter((profile) => !!profile),
        switchMap((profile: IUserAdmin) => {
          return this.actService.getDCBMServicebyIds(profile.service).pipe(
            switchMap((service: IDcbmService[]) => {
              this.dcbmServices = service;

              this.actCreationFG.controls.service.patchValue(service[0]);

              return (this.filteredServices = this.actCreationFG.controls.service.valueChanges.pipe(
                startWith(''),
                map((value) => {
                  const name = typeof value === 'string' ? value : value?.name;
                  return name ? this._filterService(name as string) : this.dcbmServices.slice();
                })
              ));
            })
          );
        })
      )
      .subscribe();

    this.subscription.add(findService);
  }

  private selectDefaultType(): Observable<I_DefaultTypes> {
    return of(DefaultTypes).pipe(
      map((defaultTypes) => {
        const foundDefaultTypeKey = Object.keys(defaultTypes).find((key) => {
          const defaultType = defaultTypes[key] as I_DefaultTypes;
          return defaultType.class === this.defaults.className;
        });

        return defaultTypes[foundDefaultTypeKey] as I_DefaultTypes;
      }),
      filter((defaultType) => !!defaultType)
    );
  }

  private updateTemplateByFilter(): Observable<void> {
    return this.actCreationFG.controls.service.valueChanges.pipe(
      filter((value) => !!value && typeof value !== 'string'),
      switchMap((service: IDcbmService) => {
        if (this.defaults.className) {
          return this.selectDefaultType().pipe(
            switchMap((actType) => {
              return this.actService.getTemplatActByDefaultTypeFilterForService(actType.id, service);
            })
          );
        } else if (this.defaults.defaultTypeIds) {
          return this.actService.getTemplateActFormActTypeIdsFilterByService(this.defaults.defaultTypeIds, service);
        } else {
          return of([]);
        }
      }),
      map((templates: ITemplateAct[]) => {
        this.templates = templates;
        const selectedTemplate: ITemplateAct = this.actCreationFG.controls.template.getRawValue() as ITemplateAct;
        if (selectedTemplate && !templates.find((t) => t._id === selectedTemplate._id)) this.actCreationFG.controls.template.patchValue('');
      })
    );
  }

  public displayTemplateFn(template: ITemplateAct): string {
    return template && template.name ? template.name : '';
  }

  public filteredTemplates: Observable<ITemplateAct[]>;

  private _filterTemplates(name: string): ITemplateAct[] {
    const filterValue = name.toLowerCase();
    return this.templates.filter((template) => template.name.toLowerCase().includes(filterValue));
  }

  public validate(): void {
    if (this.isValid()) {
      this.actService
        .createAct(this.actCreationFG.value as IActCreationParameters)
        .pipe(map((act) => (act ? this.dialogRef.close(act) : null)))
        .subscribe();
      return;
    }
  }

  public isValid(): boolean {
    const validName: boolean = this.actCreationFG.controls.name.valid;
    const validDescription: boolean = this.actCreationFG.controls.description.valid;
    const validTemplate = typeof this.actCreationFG.value.template !== 'string' && this.actCreationFG.value.template?._id ? true : false;

    return validName && validTemplate && validDescription;
  }

  public displayServiceFn(service: IDcbmService): string {
    return service && service.name ? service.name : '';
  }

  public filteredServices: Observable<IDcbmService[]>;

  private _filterService(name: string): IDcbmService[] {
    const filterValue = name.toLowerCase();
    return this.dcbmServices.filter((service) => service.name.toLowerCase().includes(filterValue));
  }

  public dcbmServices: IDcbmService[] = [];
}
